import React, { useState, useEffect } from "react";
import { motion } from 'framer-motion';
import {
  Phone,
  Video,
  Smartphone,
  MessageCircle,
  MessageSquare,
  Mail,
} from "lucide-react";

const REDIRECT_URI = process.env.REACT_APP_META_REDIRECT_URI || 'https://localhost:3002/callback';
console.log("REDIRECT_URI:", REDIRECT_URI);
console.log("All environment variables:", process.env);

const CommunicationChannels = ({
  selectedChannels = [],
  onChannelToggle = () => {},
}) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState("");
  const [facebookSDKLoaded, setFacebookSDKLoaded] = useState(false);
  const [isLoadingSDK, setIsLoadingSDK] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  const MAX_RETRY_COUNT = 5;

  const logDebug = (message, data) => {
    console.log(`[CommunicationChannels] ${message}`, data);
  };

  useEffect(() => {
    // Facebook Pixel initialization
    (function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(
      window,
      document,
      "script",
      "https://connect.facebook.net/en_US/fbevents.js"
    );

    window.fbq("init", "pixel-id-goes-here");
    window.fbq("track", "PageView");

logDebug("Environment Variables:", {
      REACT_APP_FACEBOOK_APP_ID: process.env.REACT_APP_FACEBOOK_APP_ID,
      REACT_APP_WHATSAPP_PHONE_NUMBER_ID: process.env.REACT_APP_WHATSAPP_PHONE_NUMBER_ID,
      REACT_APP_WHATSAPP_BUSINESS_ACCOUNT_ID: process.env.REACT_APP_WHATSAPP_BUSINESS_ACCOUNT_ID,
      REACT_APP_FACEBOOK_CONFIG_ID: process.env.REACT_APP_FACEBOOK_CONFIG_ID,
      META_REDIRECT_URI: process.env.META_REDIRECT_URI,
      NODE_ENV: process.env.NODE_ENV,
      REDIRECT_URI: REDIRECT_URI,
      ORIGIN: window.location.origin
    });

    const initFacebookSDK = () => {
      if (window.FB) {
        window.FB.init({
          appId: process.env.REACT_APP_FACEBOOK_APP_ID,
          cookie: true,
          xfbml: true,
          version: "v19.0",
        });

        setFacebookSDKLoaded(true);
        setIsLoadingSDK(false);
        console.log("Facebook SDK initialized successfully.");
      } else {
        console.warn("Facebook SDK not loaded. Retrying in 1 second...");
        setTimeout(initFacebookSDK, 1000);
      }
    };

    window.fbAsyncInit = initFacebookSDK;

    // Load Facebook SDK with timeout
    const loadFacebookSDK = () => {
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = "https://connect.facebook.net/en_US/sdk.js";
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);

        // Set a timeout of 30 seconds
        setTimeout(() => {
          reject(new Error("Facebook SDK load timeout"));
        }, 30000);
      });
    };

    loadFacebookSDK()
      .then(() => {
        logDebug("Facebook SDK script loaded successfully");
      })
      .catch((error) => {
        logDebug("Failed to load Facebook SDK:", error);
        setIsLoadingSDK(false);
        setConnectionError("Failed to load Facebook SDK. Please check your internet connection and try again later.");
      });

    // Cleanup function
    return () => {
      delete window.fbAsyncInit;
    };
  }, []);

  const handleFacebookSDKError = (error) => {
    logDebug("Facebook SDK error:", error);
    setIsLoadingSDK(false);
    setConnectionError("An error occurred with the Facebook SDK. Please try again later.");
  };

  const retryConnection = () => {
    if (retryCount < MAX_RETRY_COUNT) {
      setRetryCount(retryCount + 1);
      logDebug(`Retrying connection. Attempt ${retryCount + 1} of ${MAX_RETRY_COUNT}`);
      setTimeout(() => {
        launchWhatsAppSignup();
      }, 2000 * retryCount); // Increase delay with each retry
    } else {
      setConnectionError(`Maximum retry attempts (${MAX_RETRY_COUNT}) reached. Please refresh the page and try again.`);
    }
  };

  const resetRetryCount = () => {
    setRetryCount(0);
    logDebug("Retry count reset");
  };

  const launchWhatsAppSignup = async () => {
    if (isLoadingSDK) {
      setConnectionError("Facebook SDK is still loading. Please wait and try again.");
      return;
    }

    if (!facebookSDKLoaded || typeof window.FB === "undefined") {
      setConnectionError("Facebook SDK is not loaded. Please refresh the page and try again.");
      return;
    }

    setIsConnecting(true);
    setConnectionError("");
    resetRetryCount(); // Reset retry count when manually initiating signup

    if (window.FB.getAccessToken()) {
      window.FB.logout(() => {
        logDebug("Cleared previous FB login state");
      });
    } else {
      logDebug("No access token found. Skipping FB.logout.");
    }

    window.fbq("trackCustom", "WhatsAppOnboardingStart", {
      appId: process.env.REACT_APP_FACEBOOK_APP_ID,
      feature: "whatsapp_embedded_signup",
    });

    try {
window.FB.login(
        function (response) {
          setIsConnecting(false);

          if (response.authResponse && response.authResponse.code) {
            logDebug("Facebook login successful", response);
            logDebug("Redirect URI used:", REDIRECT_URI);
            logDebug("Full login response:", JSON.stringify(response, null, 2));
            logDebug("Auth Response Code:", response.authResponse.code);
            logDebug("Login redirect_uri:", REDIRECT_URI);
            logDebug("Current origin:", window.location.origin);
            logDebug("FB.login redirect_uri:", REDIRECT_URI);

fetch("/api/whatsapp/connect", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                code: response.authResponse.code,
                phone_number_id: process.env.REACT_APP_WHATSAPP_PHONE_NUMBER_ID,
                waba_id: process.env.REACT_APP_WHATSAPP_BUSINESS_ACCOUNT_ID,
                redirect_uri: REDIRECT_URI,
              }),
            })
              .then((backendResponse) => {
                if (!backendResponse.ok) {
                  return backendResponse.json().then(errorData => {
                    throw new Error(errorData.error || `HTTP error! status: ${backendResponse.status}`);
                  });
                }
                return backendResponse.json();
              })
              .then((backendData) => {
                logDebug("Backend response:", backendData);
                if (backendData && backendData.success) {
                  logDebug("Connected to backend successfully:", backendData);
                  onChannelToggle("WhatsApp");
                  setConnectionError("");
                  resetRetryCount();
                  // Store user data in local storage or state management
                  localStorage.setItem('whatsappUserData', JSON.stringify(backendData.user_data));
                  localStorage.setItem('whatsappConnectionId', backendData.connection_id);
                } else {
                  throw new Error(backendData.message || "Failed to connect WhatsApp. Please check your permissions and try again.");
                }
              })
              .catch((error) => {
                logDebug("Error connecting to WhatsApp:", error);
                setConnectionError(error.message);
                retryConnection();
              });
          } else if (response.status === "not_authorized") {
            setConnectionError("Facebook authorization failed. Please check your permissions and try again.");
            retryConnection();
          } else {
            setConnectionError("Facebook connection was cancelled or failed.");
            retryConnection();
          }
        },
        {
          config_id: process.env.REACT_APP_FACEBOOK_CONFIG_ID,
          scope: "public_profile,email,whatsapp_business_management",
          return_scopes: true,
          response_type: "code",
          override_default_response_type: true,
          extras: {
            sessionInfoVersion: 2,
          },
          auth_type: "reauthorize",
          redirect_uri: REDIRECT_URI,
        }
      );
    } catch (error) {
      logDebug("Error during WhatsApp signup:", error.message);
      setConnectionError("An unexpected error occurred during the WhatsApp connection process.");
      setIsConnecting(false);
      retryConnection();
    }
  };

  const handleRetryConnection = () => {
    setConnectionError("");
    resetRetryCount(); // Reset retry count when manually retrying
    launchWhatsAppSignup();
  };

  const isWhatsAppConnected = selectedChannels.includes("WhatsApp");

  const channels = [
    {
      name: "Phone",
      icon: Phone,
      description: "Telephone communications",
      color: "#4CAF50",
      enabled: true,
    },
    {
      name: "Web Calls",
      icon: Video,
      description: "Voice calls initiated from web",
      color: "#2196F3",
      enabled: true,
    },
    {
      name: "App Calls",
      icon: Smartphone,
      description: "Voice calls initiated from mobile app",
      color: "#9C27B0",
      enabled: true,
    },
    {
      name: "WhatsApp",
      icon: MessageCircle,
      description: "Connect through WhatsApp Business API",
      color: "#25D366",
      enabled: true,
    },
    {
      name: "Web Chat",
      icon: MessageSquare,
      description: "Embed AI chat on your website",
      color: "#FF9800",
      enabled: false,
    },
    {
      name: "App Chat",
      icon: MessageCircle,
      description: "Chat integrated within mobile app",
      color: "#E91E63",
      enabled: false,
    },
    {
      name: "Email",
      icon: Mail,
      description: "Automated email conversations",
      color: "#F44336",
      enabled: true,
    },
  ];

  const renderChannelCard = (channel) => (
    <motion.div
      key={channel.name}
      className={`flex flex-col justify-between h-full min-h-[150px] max-w-full p-4 rounded-xl cursor-pointer transition-colors duration-300 ${
        selectedChannels.includes(channel.name)
          ? 'bg-opacity-20 border-2'
          : 'bg-white border border-gray-200 hover:border-opacity-50'
      } ${!channel.enabled ? "opacity-50 cursor-not-allowed" : ""}`}
      style={{
        backgroundColor: selectedChannels.includes(channel.name) ? channel.color + '33' : 'white',
        borderColor: selectedChannels.includes(channel.name) ? channel.color : 'transparent',
        minHeight: '150px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
      variants={{
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 },
      }}
      whileHover={{ scale: 1.03, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)' }}
      whileTap={{ scale: 0.98 }}
      onClick={() => {
        if (
          channel.name === "WhatsApp" &&
          !isWhatsAppConnected &&
          !isConnecting
        ) {
          launchWhatsAppSignup().catch((error) => {
            console.error("WhatsApp signup error:", error.message);
            setConnectionError("An unexpected error occurred.");
          });
        } else if (channel.enabled && !isConnecting) {
          onChannelToggle(channel.name);
        }
      }}
      title={
        channel.name === "WhatsApp"
          ? isWhatsAppConnected
            ? "WhatsApp Connected"
            : "Click to connect with Facebook"
          : !channel.enabled
          ? "Coming Soon"
          : ""
      }
    >
      <div>
        <div className="flex items-center mb-2">
          <div className="p-2 rounded-full" style={{ backgroundColor: channel.color }}>
            {channel.name === "WhatsApp" && isConnecting ? (
              <div className="relative w-5 h-5">
                <div className="absolute inset-0 rounded-full border-2 border-white/30"></div>
                <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-white animate-spin"></div>
                <div className="absolute inset-1 rounded-full bg-white/20 animate-pulse"></div>
                <div className="absolute inset-2 rounded-full bg-white animate-ping"></div>
              </div>
            ) : (
              <channel.icon className="w-5 h-5 text-white" />
            )}
          </div>
          <h3 className="text-lg font-semibold ml-3">{channel.name}</h3>
          {channel.name === "WhatsApp" && isWhatsAppConnected && (
            <span className="ml-2 text-sm text-green-500">(Connected)</span>
          )}
          {!channel.enabled && channel.name !== "WhatsApp" && (
            <span className="ml-2 text-sm text-gray-500">(Coming Soon)</span>
          )}
        </div>
        <p className="text-gray-600 mb-2 line-clamp-2">{channel.description}</p>
      </div>
      {channel.name === "WhatsApp" &&
        connectionError &&
        !isWhatsAppConnected && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleRetryConnection();
            }}
            className="w-full mt-3 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200 text-sm font-medium"
          >
            Try Again
          </button>
        )}
    </motion.div>
  );

  return (
    <div className="max-w-7xl mx-auto  pt-3">
      <h2 className="text-2xl justify-center text-center font-bold mb-5 text-gray-800">
        Select Communication Channels
      </h2>
     {/*  {isLoadingSDK && (
        <div className="mb-4 p-4 bg-blue-100 text-blue-700 rounded-md">
          <div className="flex items-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Loading Facebook SDK... Please wait.
          </div>
        </div>
      )}
      {connectionError && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            Error: {connectionError}
          </div>
        </div>
      )}
      {isConnecting && (
        <div className="mb-4 p-4 bg-blue-100 text-blue-700 rounded-md">
          <div className="flex items-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Connecting to WhatsApp... This may take a few moments.
          </div>
        </div>
      )} */}
      <motion.div
        className="flex flex-row gap-4 max-w-7xl mx-auto min-h-[40vh] justify-center items-start"
        initial="hidden"
        animate="visible"
        variants={{
          visible: {
            transition: {
              staggerChildren: 0.1,
            },
          },
        }}
      >
        <div className="flex flex-col gap-4 flex-1 h-full justify-start">
          {channels.filter(channel => ["Phone", "Web Calls", "App Calls"].includes(channel.name)).map(renderChannelCard)}
        </div>
        <div className="flex flex-col gap-4 flex-1 h-full justify-start">
          {channels.filter(channel => ["WhatsApp", "Web Chat", "App Chat"].includes(channel.name)).map(renderChannelCard)}
        </div>
        <div className="flex flex-col gap-4 flex-1 h-full justify-start">
          {channels.filter(channel => ["Email"].includes(channel.name)).map(renderChannelCard)}
        </div>
      </motion.div>
    </div>
  );
};

export default CommunicationChannels;
